import React, { useEffect, useState, useCallback, useRef } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Box, TextField, Grid, Select, MenuItem, FormControl, InputLabel, Chip, CircularProgress, Link } from '@mui/material';

const CenteredLoadingOverlay = () => (
    <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255, 255, 255, 0.8)', zIndex: 1 }}>
        <CircularProgress size={60} />
    </Box>
);

const FieldingStatsTable = ({ apiUrl, careerStats = false }) => {
    const [playerStats, setPlayerStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedYear, setSelectedYear] = useState('');
    const [majorsFilter, setMajorsFilter] = useState('true');
    const [seasonsFilter, setSeasonsFilter] = useState('Regular Season');
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 100 });
    const [rowCount, setRowCount] = useState(0);
    const [sortModel, setSortModel] = useState([{ field: 'war', sort: 'desc' }]);
    const initialLoadComplete = useRef(false);

    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();
        params.append('page', paginationModel.page + 1);
        params.append('page_size', paginationModel.pageSize);
        params.append('career_stats', careerStats.toString());
        if (sortModel.length > 0) {
            params.append('sort_by', sortModel[0].field);
            params.append('sort_order', sortModel[0].sort);
        }
        if (searchTerm) params.append('player_name', searchTerm);
        if (selectedYear) params.append('year', selectedYear);
        if (majorsFilter && majorsFilter !== 'All') params.append('majors', majorsFilter);
        if (seasonsFilter && seasonsFilter !== 'All') {
            params.append('seasons', seasonsFilter === 'Regular Season' ? 'REGULAR' : 'POST');
        }
        return params;
    }, [paginationModel, sortModel, searchTerm, selectedYear, majorsFilter, seasonsFilter, careerStats]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setPlayerStats([]);
        try {
            const params = buildQueryParams();
            const url = `${apiUrl}?${params.toString()}`;
            const response = await fetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            const stats = data.data || data.results || data;
            const paginationInfo = data.pagination || {};
            const totalCount = paginationInfo.total_count || 0;
            const statsWithIds = stats.map((stat, index) => ({ ...stat, id: stat.hash_id || index }));
            setPlayerStats(statsWithIds);
            setRowCount(totalCount);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError(error);
        } finally {
            setLoading(false);
        }
    }, [apiUrl, buildQueryParams]);

    useEffect(() => {
        const initialize = async () => {
            await fetchData();
            initialLoadComplete.current = true;
        }
        initialize();
    }, []);

    useEffect(() => {
        if (initialLoadComplete.current) {
            fetchData();
        }
    }, [fetchData]);

    const columns = [
        { field: 'name', headerName: 'Player', width: 200, renderCell: (params) => <Link href={params.row.url} target="_blank" rel="noopener">{params.value}</Link> },
        ...(careerStats ? [] : [
            { field: 'year', headerName: 'Year', width: 90, type: 'number', valueFormatter: (params) => params.value },
            { field: 'age', headerName: 'Age', width: 80, type: 'number' },
        ]),
        { field: 'games', headerName: 'G', width: 80, type: 'number' },
        ...(careerStats ? [] : [{ field: 'war', headerName: 'WAR', width: 90, type: 'number' }]),
        { field: 'position', headerName: 'Pos', width: 80 },
        { field: 'putouts', headerName: 'PO', width: 80, type: 'number' },
        { field: 'assists', headerName: 'A', width: 80, type: 'number' },
        { field: 'double_plays', headerName: 'DP', width: 80, type: 'number' },
        { field: 'total_chances', headerName: 'TC', width: 80, type: 'number' },
        { field: 'errors', headerName: 'E', width: 80, type: 'number' },
        { field: 'fielding_percentage', headerName: 'FPCT', width: 90, type: 'number' },
        { field: 'innings_played', headerName: 'IP', width: 90, type: 'number' },
        { field: 'range_factor', headerName: 'RF', width: 90, type: 'number' },
        { field: 'zone_rating', headerName: 'ZR', width: 90, type: 'number' },
        { field: 'efficiency', headerName: 'EFF', width: 90, type: 'number' },
        { field: 'majors', headerName: 'Majors', width: 100, renderCell: (params) => <Chip label={params.value ? 'Yes' : 'No'} color={params.value ? 'success' : 'default'} size="small" variant="outlined" /> },
        { field: 'postseason', headerName: 'Season', width: 140, renderCell: (params) => <Chip label={params.value ? 'Postseason' : 'Regular'} color={params.value ? 'warning' : 'default'} size="small" variant="outlined" /> },
    ];

    if (error) return <div>Error: {error.message}</div>;

    const createDropdown = (label, value, onChange, options) => {
        const displayOptions = options.map(opt => ({ value: opt.value, label: opt.label }));
        return (
            <Grid item xs={12} sm={6} md={3} lg={2}>
                <FormControl fullWidth variant="outlined">
                    <InputLabel shrink={true}>{label}</InputLabel>
                    <Select value={value} onChange={onChange} label={label}>
                        {displayOptions.map(opt => <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>)}
                    </Select>
                </FormControl>
            </Grid>
        );
    };

    return (
        <Box>
            <Box sx={{ mb: 2 }}>
                <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: '600' }}>
                    {careerStats ? 'Career' : 'Season'} Fielding Stats
                </h2>
            </Box>
            <Box sx={{ mb: 2, p: 2, border: '1px solid lightgray', borderRadius: '4px' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} lg={4}>
                        <TextField label="Player name" variant="outlined" fullWidth value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} lg={2}>
                        <TextField label="Year" variant="outlined" fullWidth value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} placeholder="e.g., 2024" />
                    </Grid>
                    {createDropdown("Leagues", majorsFilter, (e) => setMajorsFilter(e.target.value), [
                        { value: 'All', label: 'All' }, { value: 'true', label: 'Majors' }, { value: 'false', label: 'Minors' }
                    ])}
                    {createDropdown("Season Type", seasonsFilter, (e) => setSeasonsFilter(e.target.value), [
                        { value: 'All', label: 'All' }, { value: 'Regular Season', label: 'Regular' }, { value: 'Postseason', label: 'Postseason' }
                    ])}
                </Grid>
            </Box>
            <div style={{ height: 700, width: '100%' }}>
                <DataGrid
                    rows={playerStats}
                    columns={columns}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    rowCount={rowCount}
                    paginationMode="server"
                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={setSortModel}
                    loading={loading}
                    slots={{ toolbar: GridToolbar, loadingOverlay: CenteredLoadingOverlay }}
                    disableSelectionOnClick
                />
            </div>
        </Box>
    );
};

export default FieldingStatsTable; 